import type { PrizeRoutingRequest, PrizeRoutingResult } from "./prizeRouting";
import { buildPrizeRoutingLP, decodeTrips, summarizeTrips } from "./prizeRouting";
import { solveLp } from "./highsWorker";

/**
 * Solve a prize-collecting capacitated routing instance with HiGHS, returning
 * the proven-best haul (trips + collected loot + prize). Runs in the shared Web
 * Worker so the host game's UI never freezes. Prize/weight/steps are recomputed
 * from the decoded trips, so they are exactly consistent with what the game draws.
 */
export async function solvePrizeRouting(
  req: PrizeRoutingRequest,
): Promise<PrizeRoutingResult> {
  const { timeLimitSec = 30 } = req;

  const t0 = performance.now();
  const { lp, nodes } = buildPrizeRoutingLP(req);
  const tBuildMs = performance.now() - t0;

  // tSolveMs is measured inside the worker around highs.solve() only, so it
  // never includes the one-time WASM init or the wait behind queued solves.
  const { sol, solveMs: tSolveMs } = await solveLp(lp, {
    time_limit: timeLimitSec,
    presolve: "on",
  });

  let trips: number[][] = [];
  if (sol.Status === "Optimal" || sol.Status === "Feasible") {
    const decoded = decodeTrips(nodes, (name) => {
      const col = sol.Columns[name];
      return col ? col.Primal : 0;
    });
    if (decoded) trips = decoded;
  }

  const { prize, weight, steps, collected } = summarizeTrips(req, trips);
  const status =
    sol.Status === "Optimal" ? "OPTIMAL" :
    sol.Status === "Feasible" ? "FEASIBLE" :
    String(sol.Status).toUpperCase();

  return { status, prize, weight, steps, trips, collected, tBuildMs, tSolveMs };
}
